// Congratulations screen — shown after the SF write succeeds.
//
// Merged layout (2026-06-09): the "What to Expect" video that used to live
// on a separate post-submit step now sits above the 3-column grid here, and
// the middle column toggles between the GetSetUp form (pre-submit) and the
// "Thanks for helping us…" follow-up content (post-submit). The standalone
// PostBookingComplete step is no longer rendered.

import styles from '../styles/widget.module.css';
import { formatSlotTime, formatLongDate } from '../utils/datetime';
import { countryConfigOrDefault } from '../config/countries';
import { storeDisplayName } from '../data/storeDisplayNames';
import { formatStorePhone } from '../utils/phone';
import type { BookingState } from '../state/machine';
import AddToCalendar from '../components/AddToCalendar';
import { TestCancelButton } from '../components/TestCancelButton';
import swingerLogo from '../assets/images/swinger-logo.png';

interface BookingConfirmedProps {
  state: BookingState;
  children?: React.ReactNode;
}

export function BookingConfirmed({ state, children }: BookingConfirmedProps) {
  const conf = state.confirmation!;
  const firstName = conf.customer.firstName || state.fullName.split(/\s+/)[0] || 'Guest';
  const greetingName = state.fullName.trim() || `${conf.customer.firstName} ${conf.customer.lastName ?? ''}`.trim();
  // Waitlist vs confirmed booking — the middleware prefixes waitlist IDs with
  // `WL-` and sets status to 'waitlisted'. Either signal works; we check both.
  const isWaitlist = conf.status === 'waitlisted' || conf.id.startsWith('WL-');
  // Rescheduled variant — detected via the state flag (same source as the
  // confirm screen's header), not via anything on the response, which is the
  // ordinary BookingResponse shape.
  const isReschedule = state.rescheduleMode;

  // The studio the customer actually booked with answers their questions
  // better than a national number does — and for an international customer the
  // US toll-free line is neither local nor reachable. Fall back to the
  // market-level contact only when the store has no number on file.
  const { supportContact, changeBookingNote, questionsHeading } =
    countryConfigOrDefault(state.country);
  const storePhone = state.selectedLocation?.phone?.trim();
  const storeEmail = state.selectedLocation?.email?.trim();
  const contactHref = storePhone
    ? `tel:${storePhone.replace(/[^\d+]/g, '')}`
    : supportContact.kind === 'tel'
      ? `tel:${supportContact.value}`
      : `mailto:${supportContact.value}`;
  const contactLabel = storePhone ?? supportContact.label;

  // Availability is store-level: when nobody is named, the slot comes back
  // against an "AnyFitter" bucket rather than a person. Printing that string
  // under a "Fitter" heading would read as a name, so it is suppressed and the
  // row disappears instead.
  const rawFitter = state.selectedSlot?.fitterName?.trim() ?? '';
  const fitterName = rawFitter && !/^any\s*fitter$/i.test(rawFitter) ? rawFitter : null;

  // The cancel blurb beneath already prints the studio email in the default
  // (no custom note) case, so the standalone copy in the Questions block above
  // is a duplicate — the same address twice, which CC flagged on AU. Suppress
  // the standalone whenever the blurb shows it, and where that would leave the
  // Questions block with only a heading, fill it with the market support line
  // (never the studio number — that is already printed with the address).
  const blurbShowsEmail =
    !isWaitlist && !!storeEmail && (changeBookingNote == null || changeBookingNote.includes(storeEmail));
  const questionsPhone = !storePhone
    ? { href: contactHref, label: contactLabel }
    : blurbShowsEmail && supportContact.kind === 'tel'
      ? { href: `tel:${supportContact.value}`, label: supportContact.label }
      : null;

  return (
    <div className={styles.congratsRoot}>
      <div className={`${styles.stepHeader} ${styles.congratsStepHeader}`}>
        <img
          src={swingerLogo}
          alt="Club Champion"
          className={styles.stripLogo}
          aria-hidden="true"
        />
        <div className={styles.stepTitle}>
          <h2 className={styles.congratsTitle}>
            {isReschedule
              ? 'You’re Rescheduled,'
              : isWaitlist
                ? 'You’re on the Waitlist'
                : 'Congratulations'}{' '}
            {greetingName ? greetingName.toUpperCase() : firstName.toUpperCase()}!
          </h2>
          <p className={styles.congratsSubtitle}>
            {isReschedule ? (
              <>Your appointment has been moved. Check your inbox ({conf.customer.email}).</>
            ) : isWaitlist ? (
              `We’ll reach out as soon as a slot opens (${conf.customer.email}).`
            ) : (
              <>Your game is about to get better. Check your inbox ({conf.customer.email}).</>
            )}
          </p>
        </div>
      </div>
      <hr className={styles.congratsHeaderDivider} aria-hidden="true" />

      <div className={styles.congratsGrid}>
        {/* LEFT: Get Ready! heading + intro copy + video below */}
        <div className={`${styles.congratsSection} ${styles.congratsGetReadyCol}`}>
          {!isWaitlist && (
            <>
              <h4>Get Ready!</h4>
              <p className={styles.congratsGetReadyCopy}>
                Learn more about what a Club Champion fitting is like.
              </p>
              <div className={styles.congratsVideo}>
                <iframe
                  src="https://player.vimeo.com/video/731018573"
                  title="What to expect at a Club Champion fitting"
                  frameBorder={0}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  allowFullScreen
                />
              </div>
            </>
          )}

          <div className={styles.congratsQuestionsBlock}>
            <h4>{questionsHeading ?? 'Questions?'}</h4>
            {/* This block sits under the video rather than beside the
                appointment details, because in the middle column it was being
                clipped on narrower screens — CC raised it from UK testing.
                The studio phone is already printed with the address in that
                column, so it is not repeated here; a phone appears only when
                there was no studio number, where it is the market's support
                line rather than a duplicate. */}
            {questionsPhone && (
              <a href={questionsPhone.href} className={styles.contactTel}>
                {formatStorePhone(questionsPhone.label, state.country)}
              </a>
            )}
            {/* Suppressed when the note beneath already names the same
                address — the UK's cancellation copy carries it, so it was
                printed twice in a row (CC, UK review). Testing the note rather
                than the market keeps it right for any market whose copy
                changes. */}
            {storeEmail && !blurbShowsEmail && (
              <a href={`mailto:${storeEmail}`} className={styles.contactTel}>
                {storeEmail}
              </a>
            )}
          </div>
          {/* Changing or cancelling an appointment.
              Rescheduling is self-service through the link in the confirmation
              email; cancelling is not, so it needs an address. The studio's own
              email is preferred over the market support contact for the same
              reason the phone above is — the studio that took the booking is
              the one that can change it.
              Waitlist requests are not appointments and have nothing to
              reschedule, so this is suppressed for them. */}
          {!isWaitlist && (changeBookingNote || storeEmail || supportContact.kind === 'email') && (
            <p className={styles.congratsChangeNote}>
              {changeBookingNote ?? (
                <>
                  Need to reschedule? Use the link in your confirmation email. To
                  cancel, email{' '}
                  <a href={`mailto:${storeEmail ?? supportContact.value}`}>
                    {storeEmail ?? supportContact.label}
                  </a>
                  .
                </>
              )}
            </p>
          )}

        </div>

        {/* MIDDLE: Appointment details, Add to Calendar, Questions block */}
        <div className={`${styles.congratsSection} ${styles.congratsSectionMainInfo}`}>
          <h4>
            {isReschedule
              ? 'Your Updated Appointment'
              : isWaitlist
                ? 'Your Waitlist Request'
                : 'Your Appointment'}
          </h4>
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}>
              <strong>{isWaitlist ? 'Waitlist #' : 'Confirmation:'}</strong>{' '}
              {isWaitlist ? conf.id : `#${conf.id}`}
            </p>
          </div>
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}><strong>
                {storeDisplayName(state.selectedLocation?.name ?? '', state.country)}
              </strong></p>
            {state.selectedLocation?.address && <p>{state.selectedLocation.address}</p>}
          </div>
          {storePhone && (
            <div className={styles.infoRow}>
              <p className={styles.rowLabel}><strong>Studio Phone</strong></p>
              <p>
                <a href={contactHref} className={styles.inlineTel}>
                  {formatStorePhone(storePhone, state.country)}
                </a>
              </p>
            </div>
          )}
          {/* Only when a named fitter actually took the booking. "Any Fitter"
              resolves to someone server-side, but the customer did not choose
              them, so naming them here would overstate what was agreed. */}
          {!isWaitlist && fitterName && (
            <div className={styles.infoRow}>
              <p className={styles.rowLabel}><strong>Fitter</strong></p>
              <p>{fitterName}</p>
            </div>
          )}
          <div className={styles.infoRow}>
            <p className={styles.rowLabel}>
              <strong>{isWaitlist ? 'Preferred Date' : 'Date & Time'}</strong>
            </p>
            <p>{state.selectedDate ? formatLongDate(state.selectedDate, countryConfigOrDefault(state.country).locale) : ''}</p>
            {!isWaitlist && (
              <>
                <p>{state.selectedTime ? formatSlotTime(state.selectedTime) : ''}</p>
                <AddToCalendar state={state} />
              </>
            )}
          </div>
        </div>

        {/* RIGHT: GetSetUp form (or post-submit thanks) */}
        <div className={styles.congratsSection}>
          {state.postBookingComplete ? <PostBookingThanks country={state.country} /> : children}
        </div>
      </div>

      <TestCancelButton state={state} />
    </div>
  );
}

// Middle-column content shown after the GetSetUp form is submitted —
// replaces the form in place rather than transitioning to a separate step.
function PostBookingThanks({ country }: { country: string | null }) {
  const { ballFittingOffered, fittingProcessUrl } = countryConfigOrDefault(country);
  return (
    <div className={styles.postBookingThanks}>
      <h4 className={styles.getSetUpHeading}>To get the most from your fitting…</h4>
      {/* The message names a ball fitting preference, which is a US-only
          upsell — outside the US nobody was ever asked about one, so it
          described something that had not happened. Dropped there, and the
          button moves up in its place (CC, UK review). */}
      {ballFittingOffered && (
        <p className={styles.postBookingThanksBody}>
          Thanks for helping us ensure your fitting is successful! We’ve sent your ball
          fitting preference and phone number over to your master fitter so they can
          gear up for your session. Keep an eye out for their pre-fit call soon to dial
          in your goals and kickstart your custom experience.
        </p>
      )}
      {/* Each market's own page. This was hardcoded to clubchampion.com, so a
          UK customer finishing a booking was sent to the US site. */}
      <a
        href={fittingProcessUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.button} ${styles.postBookingThanksCta} ${styles.stepButtonMobile}`}
      >
        What to Expect at Your Fitting
      </a>
    </div>
  );
}
